// src/Overview.tsx
import React from "react";
import api from "./api/client";

/** ===== Helpers (lokální datum) ===== */
const pad2 = (n: number) => n.toString().padStart(2, "0");
const startOfDayLocal = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const addDaysLocal = (d: Date, delta: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + delta, 0, 0, 0, 0);
const formatLocalDate = (d: Date) => `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;

/** ===== Typy ===== */
type Kind = { id: number; label: string; bg: string; border: string };
type Motor = { id: string; name: string };
type Job = {
  id: string;
  motorId: string;
  start: string; // ISO
  end: string;   // ISO
  color?: string;
  title?: string;
  note?: string;
  createdById?: string | null;
};

type DaySummary = {
  jobs: (Job & { isReservation: boolean })[];
  reservations: (Job & { isReservation: true })[];
};

const isReservationKindLabel = (label?: string) =>
  !!label && (/rezervace\s*svr/i.test(label) || /reservation/i.test(label));

export default function Overview() {
  const today = startOfDayLocal(new Date());
  const tomorrow = addDaysLocal(today, 1);
  const todayKey = formatLocalDate(today);
  const tomorrowKey = formatLocalDate(tomorrow);

  const [loading, setLoading] = React.useState(true);
  const [motors, setMotors] = React.useState<Motor[]>([]);
  const [kinds, setKinds] = React.useState<Kind[]>([]);
  const [byDay, setByDay] = React.useState<Record<string, DaySummary>>({});

  // map motorId -> name
  const motorName = React.useMemo(() => {
    const m = new Map<string, string>();
    motors.forEach(x => m.set(x.id, x.name));
    return (id: string) => m.get(id) || id;
  }, [motors]);

  React.useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);

        // 1) načíst motory + kinds
        const [mRes, kRes] = await Promise.allSettled([api.get("/motors"), api.get("/kinds")]);
        const motorsArr: Motor[] = (mRes.status === "fulfilled" ? (mRes.value.data || []) : []) as Motor[];
        const kindsArr: Kind[] =
          (kRes.status === "fulfilled" ? (kRes.value.data || []) : []) as Kind[];
        setMotors(motorsArr);
        setKinds(kindsArr);

        // resolver přes barvu -> kind
        const resolveKind = (color?: string): Kind | undefined => {
          if (!color) return undefined;
          const lc = String(color).toLowerCase();
          return kindsArr.find(k => String(k.bg || "").toLowerCase() === lc);
        };

        // 2) načíst jobs pro DNES + ZÍTRA
        const [jTodayRes, jTomorrowRes] = await Promise.allSettled([
          api.get("/jobs", { params: { date: todayKey } }),
          api.get("/jobs", { params: { date: tomorrowKey } }),
        ]);

        const jobsToday: Job[] =
          jTodayRes.status === "fulfilled" ? (Array.isArray(jTodayRes.value.data) ? jTodayRes.value.data : []) : [];
        const jobsTomorrow: Job[] =
          jTomorrowRes.status === "fulfilled" ? (Array.isArray(jTomorrowRes.value.data) ? jTomorrowRes.value.data : []) : [];

        const decorate = (jobs: Job[]): DaySummary => {
          const mapped = jobs.map(j => {
            const k = resolveKind(j.color);
            const isReservation =
              !!k ? isReservationKindLabel(k.label) : isReservationKindLabel(j.title || "");
            return { ...j, isReservation };
          });
          return {
            jobs: mapped,
            reservations: mapped.filter(j => j.isReservation) as (Job & { isReservation: true })[],
          };
        };

        if (!alive) return;
        setByDay({
          [todayKey]: decorate(jobsToday),
          [tomorrowKey]: decorate(jobsTomorrow),
        });
      } catch (e) {
        console.error("Overview load failed", e);
        if (!alive) return;
        setByDay({});
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [todayKey, tomorrowKey]);

  const reservationsToday = byDay[todayKey]?.reservations || [];
  const reservationsTomorrow = byDay[tomorrowKey]?.reservations || [];

  const Banner: React.FC<{
    label: string;
    items: (Job & { isReservation: true })[];
    tone: "today" | "tomorrow";
  }> = ({ label, items, tone }) => {
    if (!items.length) return null;
    const toneClasses =
      tone === "today"
        ? "border-amber-300 bg-amber-50"
        : "border-sky-300 bg-sky-50";
    const icon = tone === "today" ? "⚡" : "⏰";
    // unikátní seznam motorů
    const motorSet = Array.from(new Set(items.map(j => j.motorId)));
    return (
      <div className={`rounded-2xl border px-4 py-3 ${toneClasses}`}>
        <div className="flex items-start gap-3">
          <div className="text-xl leading-none">{icon}</div>
          <div className="min-w-0">
            <div className="font-semibold">
              {label}: Rezervace SVR u {motorSet.length} {motorSet.length === 1 ? "motoru" : "motorů"}
              {" "}({items.length} {items.length === 1 ? "blok" : "bloků"})
            </div>
            <div className="mt-2 flex flex-wrap gap-2">
              {motorSet.map(id => (
                <span
                  key={id}
                  className="rounded-full border bg-white/70 px-2 py-0.5 text-xs"
                >
                  {motorName(id)}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="max-w-[1100px] mx-auto p-4" lang="cs">
      <h1 className="mb-4 text-2xl font-semibold">Přehled</h1>

      {/* BANNERY DNES / ZÍTRA */}
      {loading ? (
        <div className="rounded-xl border px-3 py-2 text-sm text-gray-600 bg-white">Načítám…</div>
      ) : (
        <>
          <div className="space-y-3">
            <Banner label="DNES" items={reservationsToday} tone="today" />
            <Banner label="ZÍTRA" items={reservationsTomorrow} tone="tomorrow" />
          </div>

          {/* Když není nic, ukaž pozitivní stav */}
          {!reservationsToday.length && !reservationsTomorrow.length && (
            <div className="mt-3 rounded-2xl border border-emerald-300 bg-emerald-50 px-4 py-3">
              <div className="font-medium">Žádná „Rezervace SVR“ dnes ani zítra 🎉</div>
              <div className="text-sm text-emerald-700/80">Vše je volné.</div>
            </div>
          )}

          {/* Volitelně: stručný souhrn počtů bloků dnes/zítra */}
          <div className="mt-6 grid gap-3 md:grid-cols-2">
            <div className="rounded-2xl border bg-white p-4">
              <div className="text-sm text-gray-500">DNES ({todayKey})</div>
              <div className="mt-1 text-2xl font-semibold">{byDay[todayKey]?.jobs.length ?? 0}</div>
              <div className="text-xs text-gray-500">celkem bloků (všech typů)</div>
            </div>
            <div className="rounded-2xl border bg-white p-4">
              <div className="text-sm text-gray-500">ZÍTRA ({tomorrowKey})</div>
              <div className="mt-1 text-2xl font-semibold">{byDay[tomorrowKey]?.jobs.length ?? 0}</div>
              <div className="text-xs text-gray-500">celkem bloků (všech typů)</div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
